import { Stack, Typography } from '@mui/material'
import { RailVisitTrackAndTimingFormStore } from '@planning/pages/RailVisit/stores/RailVisitTrackAndTimingFormStore'
import { VesselVisitViewAttribute } from '@planning/pages/VesselVisit/Components/molecules/VesselVisitViewAttribute'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { Dayjs } from 'dayjs'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'

type RailVisitTrackAndTimingViewModeProps = {
  store: RailVisitTrackAndTimingFormStore
}

export const RailVisitTrackAndTimingViewMode: FC<RailVisitTrackAndTimingViewModeProps> = observer(
  ({ store }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    const { etaDate, etaTime, etdDate, etdTime, visitType, railTracks, railcarEstimate } = store

    const hasValue = (...values: Array<string | number | null | undefined | Dayjs>) =>
      values.some(val => {
        if (val === null || val === undefined || val === '') {
          return false
        }

        if (typeof val === 'number') {
          return val > 0
        }

        if (typeof val === 'string') {
          return val.trim().length > 0
        }

        return true
      })

    const formatDateTime = (date: Dayjs | null, time: Dayjs | null) => {
      if (!date) return undefined
      const dateStr = date.format('DD/MM/YYYY')
      const timeStr = time ? time.format('HH:mm') : '--:--'
      return `${dateStr}, ${timeStr}`
    }

    const hasAnyData =
      hasValue(etaDate.value, etaTime.value, etdDate.value, etdTime.value, railcarEstimate.value) ||
      (railTracks.value?.length ?? 0) > 0

    if (!hasAnyData) {
      return (
        <Typography variant='body2' color={theme.palette.text.secondary}>
          {t('noInformationYet', 'No information yet')}
        </Typography>
      )
    }

    return (
      <Stack direction='column'>
        {hasValue(etaDate.value, etaTime.value) && (
          <VesselVisitViewAttribute
            label={t('eta', 'ETA')}
            value={formatDateTime(etaDate.value, etaTime.value) ?? ''}
          />
        )}

        {hasValue(etdDate.value, etdTime.value) && (
          <VesselVisitViewAttribute
            label={t('etd', 'ETD')}
            value={formatDateTime(etdDate.value, etdTime.value) ?? ''}
          />
        )}

        {hasValue(visitType.value) && (
          <VesselVisitViewAttribute
            label={t('visitRecurrence', 'Visit recurrence')}
            value={visitType.value ? t(visitType.value) : ''}
          />
        )}

        {hasValue(railcarEstimate.value) && (
          <VesselVisitViewAttribute
            label={t('railcarEstimate', 'Railcar estimate')}
            value={railcarEstimate.value ? String(railcarEstimate.value) : ''}
          />
        )}

        {railTracks.value && railTracks.value.length > 0 && (
          <VesselVisitViewAttribute
            label={t('railTracks', 'Rail tracks')}
            value={railTracks.value?.map(track => track.name).join(', ') ?? ''}
          />
        )}
      </Stack>
    )
  },
)
